/**
 * The editor's view of the workflow API types.
 *
 * These were hand-written while the endpoints were excluded from the OpenAPI schema. They are now
 * published, so this module is a thin alias layer over the generated models rather than a second
 * definition of them — the names below are what the editor's twenty-odd components import, and
 * keeping them lets the underlying generated names change without touching every file.
 *
 * Two shapes need more than an alias; both are noted where they are declared.
 */

import type {
    ConditionGroupSchema,
    ActionRule as GeneratedActionRule,
    Condition as GeneratedCondition,
    ConditionExpression as GeneratedConditionExpression,
    EditorMetadata as GeneratedEditorMetadata,
    FieldTypeMeta as GeneratedFieldTypeMeta,
    LinkingRule as GeneratedLinkingRule,
    StatusMeta as GeneratedStatusMeta,
    Transition as GeneratedTransition,
    TransitionAction as GeneratedTransitionAction,
    LabelledChoice,
    ParamSpecSchema,
    WorkflowDefEditor,
    WorkflowDefList,
} from "../../apiClient/generated";

export type TransitionType = GeneratedTransition["transitionType"];
export type LogicalOperator = ConditionGroupSchema["logicalOperator"];

export type ConditionExpression = GeneratedConditionExpression;

/**
 * A node in a condition's group tree.
 *
 * A plain alias, deliberately. The generated ``children`` is ``Array<{[key: string]: any}>``
 * because the schema declares the recursion as an untyped object list rather than a
 * self-referencing component. Narrowing it here — ``Omit<..., "children"> & {children:
 * ConditionGroup[]}`` — reads better but makes this type structurally incompatible with the
 * generated ``Condition.groups`` it has to come from, which pushes casts into every consumer
 * instead of the one place that recurses. ``childGroups`` below is that one place.
 */
export type ConditionGroup = ConditionGroupSchema;

export type Condition = GeneratedCondition;

/** The children of a group, as groups. See the note on ``ConditionGroup`` for why this casts. */
export function childGroups(group: ConditionGroup): ConditionGroup[] {
    return group.children as unknown as ConditionGroup[];
}

export type ParamChoice = LabelledChoice;
export type ParamSpec = ParamSpecSchema;
export type ActionRule = GeneratedActionRule;
export type TransitionAction = GeneratedTransitionAction;
export type Transition = GeneratedTransition;
export type LinkingRule = GeneratedLinkingRule;
export type WorkflowDefinition = WorkflowDefEditor;
export type WorkflowDefinitionRow = WorkflowDefList;
export type StatusMeta = GeneratedStatusMeta;
export type FieldTypeMeta = GeneratedFieldTypeMeta;
export type EditorMetadata = GeneratedEditorMetadata;

/**
 * Selectable field names for a condition expression's field type.
 *
 * The generated ``FieldNamesMeta`` camelCases its own keys — ``extraData``, ``siteExtraData`` —
 * but an expression's ``fieldType`` still holds the wire values ``extra_data`` and
 * ``site_extra_data``, because those are data rather than field names. Indexing the object with
 * a field type directly would therefore miss, silently and at runtime, so the lookup goes
 * through here.
 */
export function fieldNamesFor(
    metadata: EditorMetadata,
    fieldType: string,
): string[] {
    switch (fieldType) {
        case "extra_data":
            return metadata.fieldNames.extraData;
        case "site_extra_data":
            return metadata.fieldNames.siteExtraData;
        default:
            return metadata.fieldNames.numeric;
    }
}
